/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React from 'react';
import FaLaptop from 'react-icons/lib/fa/laptop';
import FaPhone from 'react-icons/lib/fa/phone';
import FaMinusSquareO from 'react-icons/lib/fa/minus-square-o';
import FaPlusSquareO from 'react-icons/lib/fa/plus-square-o';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';
import { toTitleCase } from '~services/utils'; // eslint-disable-line

// Messages
import messages from './messages/ReportContacts_messages';

// Components
import Box, { Minifiable } from '../../components/Box';
import { Input, Label } from '../../components/Form';
import { InfoList, InfoRow, Key, Value } from '../../components/InfoList';

const propTypes = {
  report: PropTypes.object.isRequired,
  reportEditable: PropTypes.object,
  setReportEditable: PropTypes.func,
  editMode: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
};

const ReportContacts = ({
  editMode, intl, report, reportEditable, setReportEditable,
}) => {
  const addInfoArrayItem = fieldType => {
    const newContanctInfo = { ...reportEditable.contact_information };
    const newItem = '';
    newContanctInfo[fieldType].push(newItem);
    setReportEditable({ contact_information: newContanctInfo });
  };

  const addMobile = evnt => {
    evnt.preventDefault();
    addInfoArrayItem('mobile');
  }

  const handleAddressInputChange = (addressType, fieldType, value) => {
    const newContanctInfo = { ...reportEditable.contact_information };
    if (fieldType === 'source') {
      newContanctInfo[addressType].source = value;
    } else {
      const { [addressType]: { source } } = newContanctInfo;
      newContanctInfo[addressType][source][fieldType] = value;
    }
    setReportEditable({ contact_information: newContanctInfo });
  };

  const getAddressUpdater = (addressType, fieldType) => evnt => {
    const { target: { value }} = evnt;
    handleAddressInputChange(addressType, fieldType, value);
  }

  const handleArrayInputChange = (fieldType, idx, value) => {
    const newContanctInfo = { ...reportEditable.contact_information };
    const newValues = newContanctInfo[fieldType];
    newValues[idx] = value;
    newContanctInfo[fieldType] = [...newValues];
    setReportEditable({ contact_information: newContanctInfo });
  };

  const getMobileUpdater = idx => evnt => {
    const { target: { value }} = evnt;
    handleArrayInputChange('mobile', idx, value);
  }

  const handleBasicInputChange = (fieldType, value) => {
    const newContanctInfo = { ...reportEditable.contact_information };
    newContanctInfo[fieldType] = value;
    setReportEditable({ contact_information: newContanctInfo });
  };

  const getInfoUpdater = fieldType => evnt => {
    const { target: { value }} = evnt;
    handleBasicInputChange(fieldType, value);
  }

  const removeInfoArrayItem = (fieldType, idx) => {
    const newContanctInfo = { ...reportEditable.contact_information };
    newContanctInfo[fieldType].splice(idx, idx || 1);
    setReportEditable({ contact_information: newContanctInfo });
  };

  const getMobileRemover = idx => evnt => {
    evnt.preventDefault();
    removeInfoArrayItem('mobile', idx);
  }

  const renderAddress = (addressInfo, addressType, label) => {
    const { source } = addressInfo;
    const content = addressInfo[source];
    // No info available
    if (!content.street && !content.zip && !content.city) {
      return null;
    }

    return (
      <InfoList>
        <InfoRow id={`company-contact-${addressType}-address`}>
          <Key><Label>{label}</Label></Key>
          <Value>{`${content.street} ${content.zip} ${content.city}`}</Value>
        </InfoRow>
      </InfoList>
    );
  };

  const renderAddressEditable = (addressInfo, addressType, label) => {
    const { source: currentSource } = addressInfo;
    const { [currentSource]: editable} = addressInfo;
    const sources = ['atytj', 'local'];
    const fieldTypes = ['street', 'zip', 'city'];
    return (
      <AddressBox>
        <Label>{label}</Label>
        <AddressSource>
          {sources.map(source => (
            <Label as="label" key={`source-${source}`}>
              <Input
                className="company-contact-address-source-input"
                id={`company-contact-${addressType}-address-source-${source}-input`} // eslint-disable-line
                type="radio"
                name={`${addressType}_${source}`}
                value={source}
                checked={source === currentSource}
                onChange={getAddressUpdater(addressType, 'source')}
              />
              <FormattedMessage {...messages[`${source}_label`]} />
            </Label>
          ))}
        </AddressSource>
        <InfoList>
          {fieldTypes.map(fieldType => (
            <InfoRow
              editMode
              id={`company-contact-${addressType}-address-${fieldType}-input`}
              key={`address-${addressType}-${fieldType}`}
            >
              <Key>
                <Label as="label" htmlFor={`${addressType}-${fieldType}`}>
                  <FormattedMessage {...messages[`${fieldType}_placeholder`]} />
                </Label>
              </Key>
              <Value>
                <Input
                  id={`${addressType}-${fieldType}`}
                  value={editable ? (editable[fieldType] || '') : ''}
                  onChange={getAddressUpdater(addressType, fieldType)}
                  disabled={currentSource === 'atytj'}
                  placeholder={(
                    currentSource === 'atytj'
                    ? intl.formatMessage(
                      messages[`${fieldType}_na_placeholder`]
                    )
                    : intl.formatMessage(messages[`${fieldType}_placeholder`])
                  )}
                />
              </Value>
            </InfoRow>
          ))}
        </InfoList>
      </AddressBox>
    );
  }

  const renderMap = contactInformation => {
    const baseUrl = process.env.GMAPS_BASE_URL;
    const {
      mailing: {
        [contactInformation.mailing.source]: {
          city: mailingCity,
          street: mailingStreet,
          zip: mailingZip,
        },
      },
      visiting: {
        [contactInformation.visiting.source]: {
          city: visitingCity,
          street: visitingStreet,
          zip: visitingZip,
        },
      },
    } = contactInformation;
    let qs = null;
    if (visitingCity && visitingStreet && visitingZip) {
      qs = `
        ${toTitleCase(visitingStreet)},\u00A0
        ${toTitleCase(visitingZip)}
        ${toTitleCase(visitingCity)}\u00A0
        Finland
      `;
    } else if (mailingCity && mailingStreet && mailingZip) {
      qs = `
        ${toTitleCase(mailingStreet)},\u00A0
        ${toTitleCase(mailingZip)}
        ${toTitleCase(mailingCity)}\u00A0
        Finland
      `;
    }

    if (qs) {
      return (
        <iframe
          title="google-maps-embed"
          src={`${baseUrl}${qs}`}
          frameBorder="0"
          allowFullScreen
        />
      );
    }

    return (
      <FormattedMessage
        {...messages.map}
        info="Karttaa ei saatavilla koska osoitetta ei ole annettu tai..."
      />
    );
  }

  const renderMobiles = (content, label) => {
    // No info available
    if (!content.length) {
      return null;
    }

    return (
      <InfoList>
        {content.map(num => (
          <InfoRow
            className="company-contact-mobile"
            id={`company-contact-mobile-${num}`}
            key={`mobile-info-${num}`}
          >
            <Key><Label>{label}</Label></Key>
            {window.mobilecheck() ? (
              <Value as='a' href={`tel:${num}`}>{num}</Value>
            ) : (
              <Value>{num}</Value>
            )}
          </InfoRow>
        ))}
      </InfoList>
    );
  };

  const renderMobilesEditable = (editable, label) => (
    <InfoList>
      {editable.map((num, idx) => (
        <InfoRow
          className="company-contact-mobile-input"
          editMode
          id={`company-contact-mobile-${num}-input`}
          key={`mobile-editable-${idx}`}
        >
          <Key><Label as="label" htmlFor={`mobile-${idx}`}>{label}</Label></Key>
          <Value>
            <Input
              id={`mobile-${idx}`}
              onChange={getMobileUpdater(idx)}
              type="tel"
              value={num}
            />
            <RemoveIcon onClick={getMobileRemover(idx)} />
          </Value>
        </InfoRow>
      ))}
      <InfoRow editMode>
        <Key><Label>{label}</Label></Key>
        <Value>
          <AddIcon onClick={addMobile} />
        </Value>
      </InfoRow>
    </InfoList>
  );

  const renderPhoneField = (content, contentType, label) => {
    // No info available
    if (!content) {
      return null;
    }
    return  (
      <InfoList>
        <InfoRow id="company-contact-phone">
          <Label>{label}</Label>
          {window.mobilecheck() ? (
            <Value as='a' href={`tel:${content}`}>{content}</Value>
          ) : (
            content
          )}
        </InfoRow>
      </InfoList>
    );
  };

  const renderSingleField = (content, contentType, label) => {
    // No info available
    if (!content) {
      return null;
    }
    return (
      <InfoList>
        <InfoRow id={`company-contact-${contentType}`}>
          <Label>{label}</Label>
          <Value>{content}</Value>
        </InfoRow>
      </InfoList>
    );
  };

  const renderSingleFieldEditable = (editable, fieldType, label) => (
    <InfoList>
      <InfoRow id={`company-contact-${fieldType}-input`} editMode>
        <Label>{label}</Label>
        <Value>
          <Input value={editable || ''} onChange={getInfoUpdater(fieldType)} />
        </Value>
      </InfoRow>
    </InfoList>
  );

  const render0100100 = () => {
    const fullInfo = ((report.contact_information.phone
      || report.contact_information.mobile.length > 0)
      && report.contact_information.email);

    const blockTitle = fullInfo
      ? (
        <FormattedMessage {...messages.contact0100100Other} info="0100100" />
      ) : (
        <FormattedMessage {...messages.contact0100100Missing} info="0100100" />
      );
    const webInfo = fullInfo
      ? (
        <FormattedMessage
          {...messages.contact0100100OtherWeb}
          info="Check 0100100.fi for free"
          values={{
            url: (
              <a
                href={`https://hae.0100100.fi/?search=${report.identifier}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage
                  {...messages.contact0100100WebLabel}
                  info="0100100.fi service"
                />
              </a>
            )
          }}
        />
      ) : (
        <FormattedMessage
          {...messages.contact0100100MissingWeb}
          info="Check 0100100.fi for free"
          values={{
            url: (
              <a
                href={`https://hae.0100100.fi/?search=${report.identifier}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage
                  {...messages.contact0100100WebLabel}
                  info="0100100.fi service"
                />
              </a>
            )
          }}
        />
      );
    const phoneInfo = fullInfo
      ? (
        <FormattedMessage
          {...messages.contact0100100OtherPhone}
          info="Call 0100100 (paid service)"
          values={{
            phone:(
              window.mobilecheck() ? (
                <a href="tel:0100100">
                  <FormattedMessage
                    {...messages.contact0100100PhoneLabel}
                    info="0100100"
                  />
                </a>
              ) : (
                <FormattedMessage
                  {...messages.contact0100100PhoneLabel}
                  info="0100100"
                />
              )
            )
          }}
        />
      ) : (
        <FormattedMessage
          {...messages.contact0100100MissingPhone}
          info="Call 0100100 (paid service)"
          values={{
            phone:(
              window.mobilecheck() ? (
                <a href="tel:0100100">
                  <FormattedMessage
                    {...messages.contact0100100PhoneLabel}
                    info="0100100"
                  />
                </a>
              ) : (
                <FormattedMessage
                  {...messages.contact0100100PhoneLabel}
                  info="0100100"
                />
              )
            )
          }}
        />
      );

    return (
      <InfoList>
        <InfoRow>
          <Key><Label wrap>{blockTitle}</Label></Key>
        </InfoRow>
        <InfoRow alignLeft>
          <Key><Label><FaLaptop /></Label></Key>
          <Value>{webInfo}</Value>
        </InfoRow>
        <InfoRow alignLeft>
          <Key><Label><FaPhone /></Label></Key>
          <Value>{phoneInfo}</Value>
        </InfoRow>
      </InfoList>
    );
  }

  const mailingAddressLabel = (
    <FormattedMessage {...messages.mailingAddress} info="Postiosoite:" />
  );

  const visitingAddressLabel = (
    <FormattedMessage {...messages.visitingAddress} info="Käyntiosoite:" />
  );

  const mobilesLabel = (
    <FormattedMessage {...messages.tel} info="Matkapuhelinnumero:" />
  );

  const phoneLabel = <FormattedMessage {...messages.phone} info="Puhelin:" />;

  const faxLabel = <FormattedMessage {...messages.fax} info="Faksi:" />;

  const emailLabel = (
    <FormattedMessage {...messages.email} info="Sähköpostiosoite:" />
  );

  const websiteLabel = (
    <FormattedMessage {...messages.website} info="Kotisivu:" />
  );

  const emailContent = report.contact_information.email ? (
    <a href={`mailto:${report.contact_information.email}`}>
      {report.contact_information.email}
    </a>
  ) : null;

  const websiteContent = report.contact_information.website ? (
    <a
      href={
        report.contact_information.website.startsWith('http')
          ? `${report.contact_information.website}`
          : `http://${report.contact_information.website}`
      }
      target="_blank"
      rel="noopener noreferrer"
    >
      {report.contact_information.website}
    </a>
  ) : null;

  return (
    <Box headerLabel={<FormattedMessage {...messages.title} />}>
      <h1>{report.names.primary}</h1>

      <Contact>
        <ContactInfo editMode={editMode}>
          {editMode && !!reportEditable
            ? renderAddressEditable(
              reportEditable.contact_information.visiting,
              'visiting',
              visitingAddressLabel,
            )
            : renderAddress(
              report.contact_information.visiting,
              'visiting',
              visitingAddressLabel,
            )}
          {editMode && !!reportEditable
            ? renderAddressEditable(
              reportEditable.contact_information.mailing,
              'mailing',
              mailingAddressLabel,
            )
            : renderAddress(
              report.contact_information.mailing,
              'mailing',
              mailingAddressLabel,
            )}
          {editMode && !!reportEditable
            ? renderMobilesEditable(
              reportEditable.contact_information.mobile,
              mobilesLabel,
            )
            : renderMobiles(
              report.contact_information.mobile,
              mobilesLabel,
            )}
          {editMode && !!reportEditable
            ? renderSingleFieldEditable(
              reportEditable.contact_information.phone,
              'phone',
              phoneLabel,
            )
            : renderPhoneField(
              report.contact_information.phone,
              'phone',
              phoneLabel,
            )}
          {editMode && !!reportEditable
            ? renderSingleFieldEditable(
              reportEditable.contact_information.fax,
              'fax',
              faxLabel,
            )
            : renderSingleField(
              report.contact_information.fax,
              'fax',
              faxLabel,
            )}
          {editMode && !!reportEditable
            ? renderSingleFieldEditable(
              reportEditable.contact_information.email,
              'email',
              emailLabel,
            )
            : renderSingleField(emailContent, 'email', emailLabel)}
          {editMode && !!reportEditable
            ? renderSingleFieldEditable(
              reportEditable.contact_information.website,
              'website',
              websiteLabel,
            )
            : renderSingleField(
              websiteContent,
              'website',
              websiteLabel,
            )}
        </ContactInfo>

        <ContactExtra>
          {!editMode && render0100100()}
        </ContactExtra>
      </Contact>

      <Minifiable>
        <Map>
          {renderMap(report.contact_information)}
        </Map>
      </Minifiable>
    </Box>
  );
};

ReportContacts.propTypes = propTypes;

const AddIcon = styled(FaPlusSquareO)``;

const AddressBox = styled.div`
  margin-bottom: 12px;
`;

const Contact = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const ContactExtra = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const ContactInfo = styled.div`
  display: flex;
  flex: ${props => props.editMode ? 'auto' : 1};
  flex-direction: column;
  margin: ${props => props.editMode ? '0' : '0 0 24px'};
  width: ${props => props.editMode ? '100%' : 'auto'};

  @media (min-width: 1024px) {
    margin: ${props => props.editMode ? '0' : '0 24px 0 0'};
  }
`;

const AddressSource = styled.div`
  margin: 6px 0px;

  & > ${Label}:first-child {
    margin-right: 12px;
  }
`;

const Map = styled.div`
  margin-top: 48px;
`;

const RemoveIcon = styled(FaMinusSquareO)``;

export default injectIntl(ReportContacts);
